/**
 * BureauComparisonSection - Side-by-side bureau comparison for account details
 * Shows the same account's values as reported by each bureau
 * 
 * THEME-AWARE: Supports both light and dark modes with proper styling
 * 
 * Features:
 * - Side-by-side columns for TransUnion, Experian, Equifax
 * - Uses bureau_data when available for accurate per-bureau differences
 * - Highlights conflicts/discrepancies (rows where bureaus differ)
 * - "No Data" for missing bureau-specific values
 * - "-" for fields that are empty across all bureaus
 * - Horizontal scroll for wide tables
 */

import React from 'react';
import { AlertTriangle, CheckCircle, Scale, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getTradelineReportedBureaus } from '../../utils/tradelineBureaus';

const BUREAUS = ['TransUnion', 'Experian', 'Equifax'];

// Comparison fields with canonical keys - Complete list for dispute analysis
const COMPARISON_FIELDS = [
  { key: 'account_number', canonicalKeys: ['account_number_masked', 'masked_account_number', 'account_number'], label: 'Account Number' },
  { key: 'account_type', canonicalKeys: ['account_type'], label: 'Account Type' },
  { key: 'account_type_detail', canonicalKeys: ['account_type_detail', 'type_detail'], label: 'Account Type Detail' },
  { key: 'responsibility', canonicalKeys: ['responsibility', 'ownership'], label: 'Responsibility' },
  { key: 'original_creditor', canonicalKeys: ['original_creditor'], label: 'Original Creditor' },
  { key: 'account_status', canonicalKeys: ['account_status', 'status'], label: 'Account Status', highlight: true },
  { key: 'payment_status', canonicalKeys: ['payment_status'], label: 'Payment Status', highlight: true },
  { key: 'balance', canonicalKeys: ['balance'], label: 'Balance', format: 'currency', highlight: true },
  { key: 'credit_limit', canonicalKeys: ['credit_limit', 'limit'], label: 'Credit Limit', format: 'currency' },
  { key: 'high_credit', canonicalKeys: ['high_credit', 'highest_credit'], label: 'High Credit', format: 'currency' },
  { key: 'monthly_payment', canonicalKeys: ['monthly_payment'], label: 'Monthly Payment', format: 'currency' },
  { key: 'past_due', canonicalKeys: ['past_due', 'past_due_amount'], label: 'Past Due', format: 'currency', highlight: true },
  { key: 'date_opened', canonicalKeys: ['date_opened'], label: 'Date Opened' },
  { key: 'last_reported', canonicalKeys: ['last_reported', 'date_reported'], label: 'Last Reported' },
  { key: 'date_last_active', canonicalKeys: ['date_last_active', 'last_active'], label: 'Date Last Active' },
  { key: 'date_last_payment', canonicalKeys: ['date_last_payment', 'date_of_last_payment', 'last_payment_date'], label: 'Date of Last Payment' },
  { key: 'remarks', canonicalKeys: ['remarks', 'comments', 'comment'], label: 'Comments / Remarks' },
];

// Theme tokens for dark and light modes
const getTheme = (darkMode) => darkMode ? {
  // Dark mode - deep emerald glass
  card: { bg: 'rgba(10,34,25,.74)', border: 'rgba(142,218,182,.12)' },
  header: { bg: 'rgba(255,255,255,.04)' },
  row: { bg: 'rgba(255,255,255,.02)', alt: 'rgba(255,255,255,.028)' },
  rowDiff: { bg: 'rgba(234,178,58,.10)' },
  lineSoft: 'rgba(142,218,182,.08)',
  text: { primary: '#EAF7F0', secondary: '#C7DDD0', muted: '#93AE9F' },
  green: { pill: 'rgba(15,172,108,.14)', border: 'rgba(15,172,108,.18)', text: '#8FE0B7' },
  gold: { pill: 'rgba(234,178,58,.10)', pillStrong: 'rgba(234,178,58,.18)', border: 'rgba(234,178,58,.16)', text: '#F0C160', textStrong: '#FFD58A' },
  red: { text: '#F27A7A' },
  good: '#8FE0B7',
  summary: { bg: 'rgba(234,178,58,.08)', border: 'rgba(234,178,58,.10)' },
  noConflict: { bg: 'rgba(15,172,108,.08)', border: 'rgba(15,172,108,.12)' },
} : {
  // Light mode - premium white glass
  card: { bg: 'rgba(255,255,255,.76)', border: 'rgba(15,172,108,.10)' },
  header: { bg: 'rgba(255,255,255,.60)' },
  row: { bg: 'rgba(255,255,255,.52)', alt: 'rgba(247,252,249,.74)' },
  rowDiff: { bg: 'rgba(234,178,58,.10)' },
  lineSoft: 'rgba(15,172,108,.08)',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#6B7280' },
  green: { pill: 'rgba(15,172,108,.10)', border: 'rgba(15,172,108,.16)', text: '#0B8C58' },
  gold: { pill: 'rgba(234,178,58,.10)', pillStrong: 'rgba(234,178,58,.18)', border: 'rgba(234,178,58,.16)', text: '#C98A1B', textStrong: '#B88A15' },
  red: { text: '#E45757' },
  good: '#0B8C58',
  summary: { bg: 'rgba(234,178,58,.08)', border: 'rgba(234,178,58,.10)' },
  noConflict: { bg: 'rgba(15,172,108,.06)', border: 'rgba(15,172,108,.10)' },
};

// Get value with fallbacks from account object
const getFieldValue = (account, canonicalKeys) => {
  if (!account) return null;
  for (const key of canonicalKeys) {
    const value = account[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return null;
};

// Get bureau-specific value from bureau_data if available (case-insensitive keys)
const getBureauSpecificValue = (tradeline, canonicalKeys, bureau) => {
  const bureauData = tradeline?.bureau_data;
  if (!bureauData && !tradeline?.bureaus) return null;

  const nested = tradeline?.bureaus?.[bureau] || tradeline?.bureaus?.[bureau.toLowerCase()];
  const keyVariants = [
    bureau,
    bureau.toLowerCase(),
  ];

  for (const key of canonicalKeys) {
    if (nested && nested[key] !== undefined && nested[key] !== null && nested[key] !== '') {
      return nested[key];
    }
    const fieldData = bureauData?.[key];
    if (!fieldData || typeof fieldData !== 'object') continue;
    for (const variant of keyVariants) {
      if (fieldData[variant] !== undefined && fieldData[variant] !== null && fieldData[variant] !== '') {
        return fieldData[variant];
      }
    }
  }

  return null;
};

const getReportedBureaus = (tradeline, accounts) => {
  const reported = getTradelineReportedBureaus(tradeline);
  if (reported.length) return reported;

  return BUREAUS.filter((bureau) => {
    if (accounts?.[bureau]) return true;
    if (tradeline?.bureaus?.[bureau]) return true;
    const bureauData = tradeline?.bureau_data || tradeline?.bureauData;
    if (!bureauData) return false;
    return Object.values(bureauData).some((fieldData) => {
      if (!fieldData || typeof fieldData !== 'object') return false;
      const value = fieldData[bureau] ?? fieldData[bureau.toLowerCase()];
      return value !== undefined && value !== null && value !== '' && value !== '--';
    });
  });
};

const isBureauReported = (reportedBureaus, bureau) =>
  reportedBureaus.some((b) => b.toLowerCase() === bureau.toLowerCase());

// Format value based on type
const formatValue = (value, format) => {
  if (value === null || value === undefined || value === '' || value === '--') {
    return null;
  }

  if (format === 'currency') {
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
    if (isNaN(num)) return String(value);
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  return String(value);
};

// Check if values differ across bureaus that actually reported
const hasConflict = (values) => {
  const nonNull = values.filter((v) => v !== null && v !== undefined && v !== 'No Data' && v !== '-');
  if (nonNull.length < 2) return false;
  return new Set(nonNull.map((v) => String(v).toLowerCase())).size > 1;
};

// Get status color based on value and theme
const getStatusStyle = (status, isHighlight, theme) => {
  if (!status || status === 'No Data' || status === '-') return { color: theme.text.muted, fontStyle: 'italic' };
  if (!isHighlight) return { color: theme.text.secondary };

  const s = String(status).toLowerCase();
  if (s === 'open' || s === 'current' || s.includes('paid as agreed')) return { color: theme.good, fontWeight: 700 };
  if (s.includes('derogatory') || s.includes('collection') || s.includes('chargeoff') || s.includes('charge-off')) return { color: theme.red.text, fontWeight: 700 };
  if (s.includes('late') || s.includes('past due') || s.includes('delinquent')) return { color: theme.gold.text, fontWeight: 700 };
  return { color: theme.text.secondary };
};

// Main BureauComparisonSection Component
export const BureauComparisonSection = ({
  tradeline,
  accounts,
  accountName,
  darkMode = false,
  className
}) => {
  const theme = getTheme(darkMode);

  const hasBureauData = (
    (tradeline?.bureau_data && Object.keys(tradeline.bureau_data).length > 0)
    || (tradeline?.bureaus && Object.keys(tradeline.bureaus).length > 0)
  );
  const reportedBureaus = getReportedBureaus(tradeline, accounts);

  // NEVER fall back to account-level primary for bureau columns
  const conflicts = [];
  const fieldValues = {};

  COMPARISON_FIELDS.forEach((field) => {
    let values;

    if (hasBureauData || reportedBureaus.length > 0) {
      values = BUREAUS.map((bureau) => {
        if (!isBureauReported(reportedBureaus, bureau)) {
          return '-';
        }
        const bureauVal = getBureauSpecificValue(tradeline, field.canonicalKeys, bureau);
        if (bureauVal !== null && bureauVal !== undefined && bureauVal !== '') {
          return formatValue(bureauVal, field.format) || '-';
        }
        return '-';
      });
    } else if (accounts) {
      values = BUREAUS.map((bureau) => {
        const account = accounts[bureau];
        return account ? formatValue(getFieldValue(account, field.canonicalKeys), field.format) || '-' : '-';
      });
    } else {
      values = ['-', '-', '-'];
    }

    fieldValues[field.key] = values;

    if (hasConflict(values)) {
      conflicts.push(field.label);
    }
  });

  const bureausReported = reportedBureaus;

  const hasAnyMeaningfulData = COMPARISON_FIELDS.some((field) => {
    const values = fieldValues[field.key] || [];
    return values.some((v) => v !== null && v !== undefined && v !== '' && v !== 'No Data' && v !== '-');
  });

  const reportedCount = bureausReported.length;
  const hasMultipleBureausReporting = reportedCount >= 2;
  const canCompare = hasAnyMeaningfulData && hasMultipleBureausReporting;

  const matchBadgeLabel = (() => {
    if (!hasAnyMeaningfulData) return 'No bureau comparison available';
    if (reportedCount <= 1) return reportedCount === 1
      ? 'Reported by 1 bureau'
      : 'No bureau comparison available';
    if (conflicts.length > 0) return null;
    if (reportedCount === 2) return '2 bureaus match';
    if (reportedCount >= 3) return 'All bureaus match';
    return null;
  })();

  const displayName = accountName ||
    tradeline?.account_name ||
    tradeline?.creditor_name ||
    (accounts?.TransUnion?.account_name) ||
    'Account';

  return (
    <div
      className={cn('rounded-[20px] overflow-hidden', className)}
      style={{
        background: theme.card.bg,
        border: `1px solid ${theme.card.border}`,
        backdropFilter: 'blur(14px)',
      }}
      data-testid="bureau-comparison-section"
      data-reported-count={reportedCount}
    >
      {/* Header */}
      <div
        className="p-4 flex flex-col gap-2.5"
        style={{ borderBottom: `1px solid ${theme.lineSoft}` }}
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <Scale size={16} style={{ color: theme.gold.text }} />
            <h4
              className="text-sm sm:text-[15px] font-black tracking-tight leading-snug break-words"
              style={{ color: theme.text.primary, letterSpacing: '-0.02em' }}
            >
              <span className="block sm:inline">Bureau Comparison</span>
              {displayName && (
                <span className="block sm:inline text-[13px] sm:text-[15px] font-bold sm:font-black mt-0.5 sm:mt-0 sm:ml-1">
                  — {displayName}
                </span>
              )}
            </h4>
          </div>
          <div className="flex items-center gap-2">
            {matchBadgeLabel && (
              <div
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-extrabold"
                style={{
                  background: reportedCount <= 1 ? theme.gold.pill : theme.green.pill,
                  border: `1px solid ${reportedCount <= 1 ? theme.gold.border : theme.green.border}`,
                  color: reportedCount <= 1 ? theme.gold.text : theme.green.text,
                }}
                data-testid="bureau-match-badge"
              >
                {reportedCount <= 1 ? <Info size={14} /> : <CheckCircle size={14} />}
                <span>{matchBadgeLabel}</span>
              </div>
            )}
            {canCompare && conflicts.length > 0 && (
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-full text-[13px] font-extrabold whitespace-nowrap"
                style={{
                  background: theme.gold.pill,
                  border: `1px solid ${theme.gold.border}`,
                  color: theme.gold.text,
                }}
                data-testid="conflict-count"
              >
                <AlertTriangle size={14} />
                {conflicts.length} difference{conflicts.length > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Reporting bureaus summary */}
        <div className="flex items-center gap-2 text-[13px] flex-wrap" style={{ color: theme.text.muted }}>
          <span>Reported by:</span>
          {BUREAUS.map((bureau) => {
            const isReported = isBureauReported(bureausReported, bureau);
            return (
              <span
                key={bureau}
                className="px-2.5 py-1 rounded-full text-xs font-extrabold"
                style={{
                  background: isReported ? theme.green.pill : (darkMode ? 'rgba(255,255,255,.05)' : 'rgba(17,24,39,.04)'),
                  border: `1px solid ${isReported ? theme.green.border : (darkMode ? 'rgba(255,255,255,.06)' : 'rgba(17,24,39,.06)')}`,
                  color: isReported ? theme.green.text : theme.text.muted,
                }}
              >
                {bureau} {isReported ? '✓' : '✗'}
              </span>
            );
          })}
        </div>
      </div>

      {/* Mobile: stacked field cards (full text, no truncation) */}
      <div className="md:hidden divide-y" style={{ borderColor: theme.lineSoft }}>
        {COMPARISON_FIELDS.map((field, index) => {
          const values = fieldValues[field.key];
          const isConflict = hasConflict(values);
          const rowBg = isConflict
            ? theme.rowDiff.bg
            : index % 2 === 0
              ? theme.row.bg
              : theme.row.alt;

          return (
            <div
              key={field.key}
              className="p-3"
              style={{ background: rowBg }}
              data-testid={`comparison-row-mobile-${field.key}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-sm font-bold" style={{ color: theme.text.primary }}>
                  {field.label}
                </span>
                {isConflict && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[11px] font-extrabold"
                    style={{ background: theme.gold.pillStrong, color: theme.gold.text }}
                  >
                    Differs
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {values.map((value, vIndex) => (
                  <div
                    key={BUREAUS[vIndex]}
                    className="flex items-start justify-between gap-3 text-sm min-w-0"
                  >
                    <span
                      className="text-[10px] font-black uppercase tracking-wide flex-shrink-0 pt-0.5"
                      style={{ color: theme.text.muted }}
                    >
                      {BUREAUS[vIndex]}
                    </span>
                    <span
                      className="text-right break-words leading-snug flex-1 min-w-0"
                      style={getStatusStyle(value, field.highlight, theme)}
                    >
                      {value || '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tablet / desktop: comparison table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr style={{ background: theme.header.bg }}>
              <th 
                className="py-3.5 px-4 text-left text-xs font-black uppercase tracking-widest w-1/4"
                style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.lineSoft}` }}
              >
                Field
              </th>
              {BUREAUS.map(bureau => (
                <th 
                  key={bureau} 
                  className="py-3.5 px-4 text-left text-xs font-black uppercase tracking-widest w-1/4"
                  style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.lineSoft}` }}
                >
                  {bureau}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARISON_FIELDS.map((field, index) => {
              const values = fieldValues[field.key];
              const isConflict = hasConflict(values);
              
              const rowBg = isConflict 
                ? theme.rowDiff.bg 
                : index % 2 === 0 
                  ? theme.row.bg 
                  : theme.row.alt;
              
              return (
                <tr 
                  key={field.key}
                  style={{ background: rowBg }}
                  data-testid={`comparison-row-${field.key}`}
                >
                  <td 
                    className="py-4 px-4"
                    style={{ borderBottom: `1px solid ${theme.lineSoft}` }}
                  >
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span 
                        className="text-sm font-bold"
                        style={{ color: theme.text.primary }}
                      >
                        {field.label}
                      </span>
                      {isConflict && (
                        <span 
                          className="px-2 py-0.5 rounded-full text-[11px] font-extrabold"
                          style={{ 
                            background: theme.gold.pillStrong,
                            color: theme.gold.text,
                          }}
                          data-testid={`conflict-${field.key}`}
                        >
                          Differs
                        </span>
                      )}
                    </div>
                  </td>
                  {values.map((value, vIndex) => (
                    <td 
                      key={BUREAUS[vIndex]} 
                      className="py-4 px-4 text-sm"
                      style={{ 
                        borderBottom: `1px solid ${theme.lineSoft}`,
                        ...getStatusStyle(value, field.highlight, theme),
                      }}
                    >
                      <span className="block break-words" title={value || 'No Data'}>
                        {value || '-'}
                      </span>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Conflicts Summary */}
      {conflicts.length > 0 && (
        <div 
          className="p-4"
          style={{ 
            background: theme.summary.bg,
            borderTop: `1px solid ${theme.summary.border}`,
          }}
        >
          <div className="text-sm" style={{ color: theme.gold.text, lineHeight: 1.55 }}>
            <strong style={{ color: theme.gold.textStrong }}>⚠ Bureau differences detected:</strong><br />
            {conflicts.join(', ')}
            <span 
              className="block mt-1.5 text-[13px]"
              style={{ color: theme.text.muted }}
            >
              Differences between bureaus may help identify dispute opportunities.
            </span>
          </div>
        </div>
      )}
      
      {/* No differences message — only when multiple bureaus can be compared */}
      {conflicts.length === 0 && canCompare && (
        <div
          className="p-3"
          style={{
            background: theme.noConflict.bg,
            borderTop: `1px solid ${theme.noConflict.border}`,
          }}
        >
          <div className="flex items-center gap-2">
            <CheckCircle size={14} style={{ color: theme.green.text }} />
            <p className="text-xs" style={{ color: theme.text.muted }}>
              No bureau discrepancies detected for this account.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BureauComparisonSection;
